import React, { useEffect, useState } from 'react'
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import BeatLoader from "react-spinners/BeatLoader";
import Modal from "../../components/Modal";
import ViewTxnForm from "../home/form-view";
import { toast } from "react-toastify";

import Constant from "../../Constant";
import { MdRefresh } from 'react-icons/md';
import { FaRegEye } from 'react-icons/fa';
import { fetchUserTransactions } from "../../services/users";

const columns = [
    {
        id: 'type',
        label: 'Type',
        minWidth: 100
    },
    {
        id: 'client',
        label: 'Client ',
        minWidth: 100
    },
    {
        id: 'agent',
        label: 'Agent ',
        minWidth: 100
    },
    {
        id: 'amount',
        label: 'Amount',
        minWidth: 80,
    },
    {
        id: 'reference',
        label: 'Reference',
        minWidth: 50,
    },
    {
        id: 'status',
        label: 'Status',
        minWidth: 80,
    },
    {
        id: 'created_at',
        label: 'Created at',
        minWidth: 60,
    },
    {
        id: 'actions',
        label: 'Actions',
        minWidth: 60,
        align: 'right',
    },
];

const UserTransactions = ({ userId, authToken, refreshKey = 0 }) => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [transactions, setTransactions] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currentTransaction, setCurrentTransaction] = React.useState(null);

    const modalViewRef = React.useRef(null);
    const transFormUpdateRef = React.useRef(null);

    const loadTransactions = (token, pageIndex = 0, pageSize = 10) => {
        if (!userId || !token) return;
        const offset = Number(pageIndex) * Number(pageSize);
        const limit = Number(pageSize) || 10;
        setLoading(true);
        fetchUserTransactions(userId, token, { offset, limit })
            .then((res) => {
                if (res.status_code == 200) {
                    setTransactions(res.data?.transactions || []);
                    setTotalCount(Number(res.data?.total) || 0);
                } else if (
                    res.status_code == 404 ||
                    /no transactions/i.test(res.message || "")
                ) {
                    setTransactions([]);
                    setTotalCount(0);
                } else {
                    setTransactions([]);
                    setTotalCount(0);
                    toast.error(`${res.message}`);
                }
            })
            .catch((err) => {
                console.log("ERROR: ", err);
                setTransactions([]);
                setTotalCount(0);
                toast.error("Failed to load user transactions");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (!authToken || !userId) return;
        loadTransactions(authToken, page, rowsPerPage);
    }, [authToken, userId, page, rowsPerPage, refreshKey]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleRefresh = () => {
        if (!authToken) return;
        loadTransactions(authToken, page, rowsPerPage);
    };

    const handleView = (tx) => {
        modalViewRef.current.open();
        setCurrentTransaction(tx);
    };

    const closeViewTrans = () => {
        modalViewRef.current.close();
    };

    const getCreatedAt = (date) => `${new Date(date).toDateString()}, ${new Date(date).toLocaleTimeString()}`;

    const getPersonLabel = (person) => {
        if (!person) return "—";
        const name = person.first_name || person.last_name || "-";
        return `${name} (${person.username || person.phone_number || "—"})`;
    };

    const getReferenceLabel = (transaction) => {
        if (transaction.code) return transaction.code;
        if (!transaction.request_data) return "-";
        try {
            const parsed = JSON.parse(transaction.request_data);
            return parsed.reference || parsed.reason || transaction.request_data;
        } catch (_err) {
            return transaction.request_data;
        }
    };

    return (
        <div className="md:mt-4 mx-10 sm:mt-20">
            <div className="flex justify-end mb-3">
                <button
                    type="button"
                    className="bg-[#405B9C] text-[#fff] rounded-[5px] px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-60"
                    onClick={handleRefresh}
                    disabled={loading || !authToken}
                >
                    <MdRefresh size={16} className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 500, minWidth: 600 }} style={{ boxShadow: 'none' }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align="left"
                                        style={{ minWidth: column.minWidth }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && transactions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        No transactions found.
                                    </TableCell>
                                </TableRow>
                            )}
                            {transactions.map((transaction) => {
                                    return (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={transaction.id}>
                                            <>
                                                <TableCell align="left">
                                                    {transaction.type}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {getPersonLabel(transaction.client)}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {getPersonLabel(transaction.agent)}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {`${transaction.amount} ${transaction.currency?.name || transaction.currency?.code || ""}`}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {getReferenceLabel(transaction)}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {transaction.status}
                                                </TableCell>
                                                <TableCell align="left">
                                                    {getCreatedAt(transaction.created_at)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <div className="flex gap-2">
                                                        <button
                                                            type="button"
                                                            className="bg-[#2DE2EE] rounded-full text-[#fff] p-2" tooltip="View Txn"
                                                            onClick={() => handleView(transaction)}
                                                        >
                                                            <FaRegEye size={16} color="#000" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <BeatLoader
                    color={Constant.spinner_color}
                    loading={loading}
                    cssOverride={{
                        textAlign: 'center',
                        marginTop: '20px'
                    }}
                />
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Modal
                title="Transaction Details"
                onSubmit={() => closeViewTrans()}
                onClose={() => null}
                submitBtn="Close"
                cancelBtn=""
                ref={modalViewRef}
            >
                <ViewTxnForm
                    ref={transFormUpdateRef}
                    data={currentTransaction}
                />
            </Modal>
        </div>
    )
}

export default UserTransactions
